class CustomSearchPopup extends HTMLElement {
  constructor() {
    super();
    this.body = document.body;
    this.openBtn = this.body.querySelector(".js-search-header--custom");
    this.searchContainer = this.body.querySelector(".site-header__search-container");
    this.searchBackground = this.body.querySelector(".predictive__screen");
    this.backBtn = this.body.querySelector(".btn-back-search--custom");
    this.clearBtn = this.body.querySelector(".btn-close-search--custom");
    this.input = this.body.querySelector('#Search');
    this.resultsHeading = this.querySelector(".search-content__products .heading");
    this.originalResultsHeading = this.resultsHeading.textContent || '';
    this.resultsContainer = this.querySelector('#SearchResults');
    this.originalResultsHTML = this.resultsContainer?.innerHTML || '';
    this.viewAllContainer = this.querySelector('#ViewAllContainer');
    this.debouncedHandleSearchInput = this.debounce(this.handleSearchInput.bind(this), 500);
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initSwiper();
  }

  setupEventListeners() {
    this.clearBtn.addEventListener("click", this.clearSearchResults.bind(this));
    this.input.addEventListener('input', this.debouncedHandleSearchInput);
    
    this.openBtn?.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.searchContainer.appendChild(this);
      this.body.appendChild(this.searchContainer);
      this.body.classList.add('no-scroll');
      this.classList.remove('hide');
      this.searchBackground.classList.add("is-active");
      this.searchContainer.classList.add("is-active");
    });

    this.backBtn.addEventListener("click", () => {
      this.clearSearchResults();
      this.body.classList.remove('no-scroll');
      this.searchContainer.classList.remove("is-active");
      this.classList.add("hide");
      this.searchBackground.classList.remove("is-active");
    });
  }
  
  getSaleInfo(product) {
    const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
    if (isNaN(priceInCents)) {
      return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
    }

    let metafieldDiscount = 0;
    let isFixedAmount = false;

    // Priority 1: Fixed Amount Off
    const rawAmount = parseFloat(product.amountOff?.value) || 0;
    if (rawAmount > 0) {
      metafieldDiscount = Math.round(rawAmount * 100);
      isFixedAmount = true;
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 2: Product Percentage Off
    const rawOff = parseFloat(product.percentOff?.value) || 0;
    if (rawOff > 0) {
      metafieldDiscount = Math.round((priceInCents * rawOff) / 100);
      return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
    }

    // Priority 3: Collection Percentage Off
    const collections = product.collections?.edges || [];
    for (const { node: collection } of collections) {
      const collectionOff = parseFloat(collection.collectionOff?.value) || 0;
      if (collectionOff > 0) {
        metafieldDiscount = Math.round((priceInCents * collectionOff) / 100);
        return { metafieldDiscount, isFixedAmount, hasMetafieldDiscount: true };
      }
    }

    return { metafieldDiscount: 0, isFixedAmount: false, hasMetafieldDiscount: false };
  }

  clearSearchResults() {
    this.input.value = '';
    this.clearBtn.classList.add('hide');
    this.resultsContainer.innerHTML = this.originalResultsHTML;
    this.resultsContainer.classList.remove('search-results');
    this.resultsHeading.textContent = this.originalResultsHeading;
    this.viewAllContainer.innerHTML = '';
  }

  debounce(fn, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  async handleSearchInput() {
    const query = this.input.value.trim();
  
    if (query.length === 0) {
      this.clearSearchResults();
      return;
    }

    this.clearBtn.classList.remove('hide');
  
    if (query.length < 2) {
      this.resultsContainer.innerHTML = '';
      this.viewAllContainer.innerHTML = '';
      return;
    }
  
    try {
      this.resultsHeading.innerHTML = `<div class="loader"></div>`;
      this.viewAllContainer.innerHTML = '';

      const graphqlQuery = `
        query search($query: String!) {
          search(query: $query, first: 10, types: PRODUCT) {
            totalCount
            edges {
              node {
                ... on Product {
                  id
                  title
                  handle
                  onlineStoreUrl
                  availableForSale
                  featuredImage {
                    url
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  compareAtPriceRange {
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  amountOff: metafield(namespace: "custom", key: "amount_off") {
                    value
                  }
                  percentOff: metafield(namespace: "custom", key: "off") {
                    value
                  }
                  collections(first: 50) {
                    edges {
                      node {
                        title
                        collectionOff: metafield(namespace: "custom", key: "off") {
                          value
                        }
                      }
                    }
                  }
                  tags
                }
              }
            }
          }
        }
      `;

      const response = await fetch('https://dalstrong.ca/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': 'a7e339ad22be0bfa26296383c45a76c9'
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: { query }
        })
      });

      const json = await response.json();
      if (json.errors) {
        throw new Error(json.errors[0].message);
      }

      const searchData = json.data.search;
      const results = searchData.edges.map(edge => edge.node);
      const keywords = ['series', 'edition', 'collection', 'dalstrong'];
  
      this.resultsContainer.classList.add('search-results');
      this.resultsContainer.innerHTML = results.map(product => {
        if (product.handle.includes("mystery-gift")) {
          return '';
        }
        const titleValues = product.title.split('|').map(s => s.trim());
        let seriesIndex = 1;
  
        for (let i = 0; i < titleValues.length; i++) {
          const lowerVal = titleValues[i].toLowerCase();
          if (keywords.some(keyword => lowerVal.includes(keyword))) {
            seriesIndex = i;
            break;
          }
        }

        const priceInCents = Math.round(parseFloat(product.priceRange.minVariantPrice.amount) * 100);
        const compareAtPrice = product.compareAtPriceRange?.maxVariantPrice?.amount ? parseFloat(product.compareAtPriceRange.maxVariantPrice.amount) : null;
        const compareAtPriceInCents = compareAtPrice ? Math.round(compareAtPrice * 100) : 0;
        const currency = product.priceRange.minVariantPrice.currencyCode;

        const { metafieldDiscount, isFixedAmount, hasMetafieldDiscount } = this.getSaleInfo(product);

        const isLimitedTimeDeal = hasMetafieldDiscount;
        const finalPriceInCents = hasMetafieldDiscount ? (priceInCents - metafieldDiscount) : priceInCents;
        const finalPrice = finalPriceInCents / 100;

        // Determine original price for strikethrough
        let originalPrice = null;
        if (hasMetafieldDiscount) {
          // Strikethrough anchor: regular price for fixed amount_off, otherwise the higher of compare_at or variant price
          originalPrice = (compareAtPriceInCents > priceInCents && !isFixedAmount ? compareAtPriceInCents : priceInCents) / 100;
        } else if (compareAtPriceInCents > priceInCents) {
          originalPrice = compareAtPriceInCents / 100;
        }

        const formatPrice = (amt) => {
          return amt.toLocaleString('en-US', { style: 'currency', currency: currency }).replace(/\.00$/, '');
        };
  
        const series = titleValues[seriesIndex] || '';
        const mainTitle = titleValues[0] || '';
        
        return `
          <div class="predictive-result">
            <a href="${product.onlineStoreUrl}">
              <div class="search-content__product-image">
                <img src="${product.featuredImage?.url || ''}" alt="${product.title}" width="40">
              </div>
              <div class="search-content__product-content-container">
                <p class="search-content__product-series">${series}</p>
                <p class="search-content__product-title">${mainTitle}</p>
                <div class="search-content__product-price-container">
                  <p class="search-content__product-price${isLimitedTimeDeal ? ' discounted' : ''}">$${finalPrice.toFixed(2)}</p>
                  ${originalPrice ? `<p class="search-content__product-compare-at-price">$${originalPrice.toFixed(2)}</p>` : ''}
                </div>
              </div>
            </a>
          </div>
        `;
      }).join('');

      const count = searchData.totalCount;
      if (typeof count === 'number') {
        this.resultsHeading.textContent = `${count} result${count === 1 ? '' : 's'}`;
        this.viewAllContainer.innerHTML = `
          <a href="/search?q=${encodeURIComponent(query)}&type=product" class="predictive-view-all__link">
            View all
          </a>
        `;
      } else {
        this.resultsHeading.textContent = this.originalResultsHeading;
        this.viewAllContainer.innerHTML = '';
      }
  
    } catch (error) {
      console.error('Error fetching search results:', error);
      this.resultsContainer.innerHTML = '';
      this.viewAllContainer.innerHTML = '';
      this.resultsHeading.textContent = this.originalResultsHeading;
    }
  }  

  initSwiper() {
    const swiperElement = this.querySelector('.search-content__content .swiper');
    if (swiperElement) {
      new Swiper(swiperElement, {
        slidesPerView: 1,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }
      });
    }
  }
}

customElements.define('custom-search-popup', CustomSearchPopup);